import {
  mapPrognosisDbToDomain,
  PrognosisDomain,
  mapPrognosisDtoToDb,
  mapPrognosisDtoToDomain,
} from "@/types/Prognosis";
import { prognosisApi } from "@/api/prognosisApi";
import { contentUpdatingManager } from "@/api/contentUpdatingManager";
import { isDayPassed } from "@/utils/isDayPassed";
import prognosisDao from "@/api/prognosisDao";
import { iso8601ToDate } from "@/utils/iso8601ToDate";

interface PrognosisRepository {
  getAstroPrognosis: () => Promise<PrognosisDomain[]>;
  getZodiacPrognosis: () => Promise<PrognosisDomain[]>;
  getYearPrognosis: () => Promise<PrognosisDomain | null>;
  clearAllPrognosis: () => void;
}

export const usePrognosisRepository = (): PrognosisRepository => {
  return prognosisRepository;
};

const prognosisRepository: PrognosisRepository = {
  getAstroPrognosis: async (): Promise<PrognosisDomain[]> => {
    const lastUpdating =
      contentUpdatingManager.getAstroPrognosisDataLastUpdating();
    const dbPrognosis = await prognosisDao.getAllAstroPrognosis();
    if (
      !lastUpdating ||
      isDayPassed(lastUpdating) ||
      dbPrognosis.length === 0
    ) {
      const newPrognosis = await prognosisApi.getAstroPrognosis();
      prognosisDao.clearAllAstroPrognosis();
      const newDate = newPrognosis[0].endDate;
      prognosisDao.saveAllAstroPrognosis(newPrognosis.map(mapPrognosisDtoToDb));
      contentUpdatingManager.setAstroPrognosisDataLastUpdating(iso8601ToDate(newDate ?? "") ?? new Date());
      return newPrognosis.map(mapPrognosisDbToDomain);
    }
    return dbPrognosis.map(mapPrognosisDbToDomain);
  },

  getZodiacPrognosis: async (): Promise<PrognosisDomain[]> => {
    const lastUpdating =
      contentUpdatingManager.getZodiacPrognosisDataLastUpdating();
    const dbPrognosis = await prognosisDao.getAllZodiacPrognosis();
    if (
      !lastUpdating ||
      isDayPassed(lastUpdating) ||
      dbPrognosis.length === 0
    ) {
      const newPrognosis = await prognosisApi.getZodiacPrognosis();
      prognosisDao.clearAllZodiacPrognosis();
      prognosisDao.saveAllZodiacPrognosis(
        newPrognosis.map(mapPrognosisDtoToDb)
      );
      const newDate = newPrognosis[0].endDate;
      contentUpdatingManager.setZodiacPrognosisDataLastUpdating(iso8601ToDate(newDate ?? "") ?? new Date());
      return newPrognosis.map(mapPrognosisDbToDomain);
    }
    return dbPrognosis.map(mapPrognosisDbToDomain);
  },

  getYearPrognosis: async (): Promise<PrognosisDomain | null> => {
    const lastUpdating =
      contentUpdatingManager.getYearPrognosisDataLastUpdating();
    const dbPrognosis = prognosisDao.getYearPrognosis();
    if (!lastUpdating || isDayPassed(lastUpdating) || !dbPrognosis) {
      const newPrognosis = await prognosisApi.getYearPrognosis();
      prognosisDao.clearYearPrognosis();
      prognosisDao.saveYearPrognosis(mapPrognosisDtoToDb(newPrognosis));
      contentUpdatingManager.setYearPrognosisDataLastUpdating(new Date());
      return mapPrognosisDtoToDomain(newPrognosis);
    }
    return mapPrognosisDbToDomain(dbPrognosis);
  },

  clearAllPrognosis: () => {
    contentUpdatingManager.clearAllDataLastUpdating();
    prognosisDao.clearAllAstroPrognosis();
    prognosisDao.clearAllZodiacPrognosis();
    prognosisDao.clearYearPrognosis();
    prognosisDao.clearAllPrognosis();
  },
};
